// @optimyl/telemetry — Application Insights setup, shared so every Optimyl web app reports the same way.
//
// This is deliberately shared rather than copied: two apps instrumenting differently is an operational
// problem, not a style one. It shows up as dashboards that silently disagree, and as an incident where the
// app you need data from is the one that wasn't collecting it.
//
// The employer intake and Skynet Replay apps each grew their own copy of this module before it was
// extracted, and the copies had drifted apart in ways worth recording, because the same forks will
// reappear if this file is edited casually:
//
//   - properties were attached to the telemetry item in one and passed as a second `customProperties`
//     argument in the other. Both reach App Insights as custom dimensions; only one form is used here.
//   - severity was a `SeverityLevel` enum member in one and a bare `2` in the other.
//   - one mirrored everything to the console behind an `import.meta.env.DEV` check, the other logged
//     errors unconditionally.
//
// Console behaviour here is settled deliberately: errors and warnings always mirror to the console, so a
// misconfigured connection string never means a silent failure, while events and page views do not, so the
// console stays readable. No `import.meta.env` — this package stays free of bundler-specific globals.

import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";

let appInsights: ApplicationInsights | null = null;

export interface TelemetryOptions {
  /** From config.json. When empty, telemetry is disabled and every call below is a no-op. */
  connectionString?: string;
  /**
   * Value for the `ai.cloud.role` tag — this app's own name (e.g. "Optimyl.Crm.UI"). Required, because
   * telemetry from several apps lands in one resource and is otherwise impossible to separate.
   */
  roleName: string;
}

export function initAppInsights({ connectionString, roleName }: TelemetryOptions): void {
  if (!connectionString) {
    console.warn(`[Telemetry] No connection string — App Insights disabled for ${roleName}`);
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableFetchTracking: false,
    },
  });

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer((item) => {
    item.tags = item.tags ?? [];
    item.tags["ai.cloud.role"] = roleName;
  });
}

export function setAuthenticatedUser(userId: string, accountId?: string): void {
  appInsights?.setAuthenticatedUserContext(userId, accountId, true);
}

export function clearAuthenticatedUser(): void {
  appInsights?.clearAuthenticatedUserContext();
}

export function trackError(error: Error, properties?: Record<string, string>): void {
  appInsights?.trackException({ exception: error, severityLevel: SeverityLevel.Error, properties });
  console.error(error);
}

export function trackWarning(message: string, properties?: Record<string, string>): void {
  appInsights?.trackTrace({ message, severityLevel: SeverityLevel.Warning, properties });
  console.warn(message);
}

export function trackEvent(name: string, properties?: Record<string, string>): void {
  appInsights?.trackEvent({ name, properties });
}

export function trackPageView(name: string): void {
  appInsights?.trackPageView({ name });
}
